"use client"
import NextLink from "next/link"
import { AppRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime"
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types"
import { forwardRef, useContext, useRef, type ComponentProps, type FocusEvent, type PointerEvent, type TouchEvent } from "react"

type IntentLinkProps = Omit<ComponentProps<typeof NextLink>, "prefetch" | "href"> & { href: string }

// A pointer that rests this long on a link is aiming at it; one sweeping across a list is not.
const HOVER_INTENT_MS = 80

/**
 * A link that fetches its page when the user shows intent: hover, focus or touch. Viewport prefetch ran the
 * Workspace layout on the Worker for every link in view, clicked or not. The full prefetch also brings the page's
 * data, so the click that follows renders from the router cache.
 */
export const IntentLink = forwardRef<HTMLAnchorElement, IntentLinkProps>(function IntentLink(
  { href, onPointerEnter, onPointerLeave, onFocus, onTouchStart, ...props },
  ref,
) {
  // Server-rendered tests and previews have no app router; they render the link without prefetch.
  const router = useContext(AppRouterContext)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // Only same-tab page links: an API route would run its handler, and a new tab cannot use this router's cache.
  const pageLink = href.startsWith("/") && !href.startsWith("/api/") && props.target !== "_blank"
  const prefetch = () => {
    if (router && pageLink) router.prefetch(href, { kind: PrefetchKind.FULL })
  }
  return <NextLink ref={ref} href={href} prefetch={false} {...props}
    onPointerEnter={(event: PointerEvent<HTMLAnchorElement>) => {
      clearTimeout(timer.current)
      timer.current = setTimeout(prefetch, HOVER_INTENT_MS)
      onPointerEnter?.(event)
    }}
    onPointerLeave={(event: PointerEvent<HTMLAnchorElement>) => { clearTimeout(timer.current); onPointerLeave?.(event) }}
    onFocus={(event: FocusEvent<HTMLAnchorElement>) => { prefetch(); onFocus?.(event) }}
    onTouchStart={(event: TouchEvent<HTMLAnchorElement>) => { prefetch(); onTouchStart?.(event) }} />
})
